import { lodash } from '@avantia/lodash';
import { createControlId } from '@avantia/questionset-model';
import { HpClientDebugFlags } from '../../../clientDebugFlags';
import { logWarning } from '../../../clientLogging';
import { getDisplayTextFromLookupItem } from '../../../tools/listSearchUtility';
import { HpClientStandardInputTextAndStyle } from '../../../types';
import { HpInputControlProps } from '../../../types/inputControlTypes';
import { IconWidget, ImageWidget } from '../../widgets/index';
import { HpFormGroupClassTypes, StandardFormGroupWidget } from '../../widgets/standardFormGroupWidget';
import { createSpaceKeyAsClickHandler } from '../controlEvents';
import { iterateLookupMap } from '../controlUtilities';
import { createStandardFormEvent, createStandardMouseEvent } from '../inputControl';

export type HpCreateCustomOptionWidgetFunc = (lookupKey: string) => JSX.Element;

export interface HpCommonImageButtonListControlProps extends HpInputControlProps {
  title?: string;
  listType: 'CheckBox' | 'Radio';
  createCustomOptionWidget?: HpCreateCustomOptionWidgetFunc;
}

export function CommonImageButtonListControl({
  answerSource,
  createCustomOptionWidget,
  data,
  dataType,
  disabled,
  errors,
  formatType,
  id,
  listType,
  onChange,
  onMouseEnter,
  onMouseLeave,
  onMouseOver,
  question,
  questionText,
  subtext,
  title,
  ui,
  value
}: HpCommonImageButtonListControlProps): JSX.Element {
  const { lookup } = data;
  const listTypeLower = listType.toLowerCase();
  const textAndStyle = data.textAndStyle[formatType] as HpClientStandardInputTextAndStyle;
  const error = errors ? errors[id] : undefined;
  const options: JSX.Element[] = [];
  const { layout } = textAndStyle;
  const mouseOverEvent = createStandardMouseEvent(onMouseOver, question);
  const mouseEnterEvent = createStandardMouseEvent(onMouseEnter, question);
  const mouseLeaveEvent = createStandardMouseEvent(onMouseLeave, question);
  const changeEvent = createStandardFormEvent(onChange, question);

  title = title || 'Select a value';
  const values = lodash.isArray(value) ? (value as string[]) : [value];

  if (lookup) {
    iterateLookupMap(lookup, ui, (option, key) => {
      const { image, icon, iconWidth, description } = option || {};
      const itemKey = createControlId({ questionId: id, key });
      const checked = values.indexOf(key) >= 0;
      const optionText = getDisplayTextFromLookupItem(option, key);
      if (key === value) {
        title = optionText;
      }

      let imageWidget: JSX.Element | undefined;
      if (image) {
        imageWidget = <ImageWidget image={image} altText={optionText || key} selected={checked} />;
      } else if (icon) {
        imageWidget = <IconWidget icon={icon} width={iconWidth || 3} />;
      } else {
        logWarning(
          `The Image${listType}ButtonListControl with id ${id} has no specified image or icon for its "${key}" option.`,
          HpClientDebugFlags.Controls
        );
      }

      let optionTextWidget: JSX.Element | null = null;
      if (createCustomOptionWidget) {
        optionTextWidget = createCustomOptionWidget(key);
      } else {
        optionTextWidget = (
          <div>
            <span>{optionText}</span>
            {description && <span>{description}</span>}
          </div>
        );
      }

      const itemId = createControlId({ questionId: id });
      const onKeyPressFunc = disabled ? undefined : createSpaceKeyAsClickHandler(changeEvent);
      const item = (
        <label
          key={itemKey}
          id={itemKey}
          className={checked ? 'hp-checked' : undefined}
          onMouseOver={mouseOverEvent}
          onMouseEnter={mouseEnterEvent}
          onMouseLeave={mouseLeaveEvent}
          tabIndex={0}
          onKeyPress={onKeyPressFunc}
        >
          <input
            checked={checked}
            disabled={disabled === true}
            id={itemId}
            name={optionText}
            onChange={changeEvent}
            tabIndex={-1}
            title={title}
            type={listTypeLower}
            value={key}
          />
          {imageWidget}
          {optionTextWidget}
        </label>
      );
      options.push(item);
    });
  }

  let groupClassName: string;
  if (layout === 'vertical' || layout === 'vertical-left') {
    groupClassName = 'btn-group-vertical';
  } else {
    groupClassName = 'hp-float';
  }

  return (
    <StandardFormGroupWidget
      id={id}
      question={question}
      questionText={questionText}
      control={
        <div className={groupClassName} role="group">
          {options}
        </div>
      }
      textAndStyle={textAndStyle}
      error={error}
      controlType={`image-${listTypeLower}-button-list` as HpFormGroupClassTypes}
      dataType={dataType}
      answerSource={answerSource}
      helpInfo={data.helpInfo}
      helpInfoUI={ui.helpInfo}
      subtext={subtext}
      onClick={onChange}
    />
  );
}
